import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import localforage from 'localforage';
import { UPLOAD_BASE_64 } from 'graph_ql/createStyle/mutation';
import { CREATE_ANSWERS } from 'graph_ql/getQuiz/mutation';
import { GENERATE_MODEL_IMAGE } from 'graph_ql/users/mutation.gql';
import { UPDATE_PROFILE } from 'graph_ql/authentication/mutation';
import { USER_DETAIL } from 'graph_ql/authentication/queries';
import { getMe, setGeneratingUserImage } from '../../redux/authentication/user_detail';
import { useAuthorize } from '../../hooks/useAuthorize';
import { useLocalUser } from '../../hooks/useLocalUser';
import { LOCAL_FORAGE_KEY } from '../../utils/constant';

interface UseImageUploadProps {
    userId: string;
    onUploadComplete?: (modelImageUrl: string) => void;
    setSharedModelImageUrl: (url: string) => void;
}

interface ImageData {
    mimeType: string;
    data: string;
}

export const useImageUpload = ({ userId, onUploadComplete, setSharedModelImageUrl }: UseImageUploadProps) => {
    const dispatch = useDispatch();
    const authorize = useAuthorize();
    const { localUser, saveLocalUser } = useLocalUser();

    const [generateModelImageMutation, { loading: generatingModelImage }] = useMutation(GENERATE_MODEL_IMAGE, {
        context: {
            allowAnonymousAuth: true,
        },
        onCompleted(response) {
            const newModelImageUrl = response.generateModelImage.modelImageUrl;
            setSharedModelImageUrl(newModelImageUrl);
            const updatedLocalUser = {
                ...localUser,
                modelImageUrl: newModelImageUrl,
            };
            saveLocalUser(updatedLocalUser);
            dispatch(setGeneratingUserImage(false));
            if (onUploadComplete) {
                onUploadComplete(newModelImageUrl);
            }
        },
        onError(error) {
            dispatch(setGeneratingUserImage(false));
            console.error('Error generating model image:', error);
        },
    });

    const [updateProfileMutation, { loading: updateProfileLoading }] = useMutation(UPDATE_PROFILE, {
        onCompleted(response) {
            const newModelImageUrl = response.updateProfile.modelImageUrl;
            setSharedModelImageUrl(newModelImageUrl);
            dispatch(getMe(response.updateProfile));
            dispatch(setGeneratingUserImage(false));
            if (onUploadComplete) {
                onUploadComplete(newModelImageUrl);
            }
        },
        refetchQueries: [{ query: USER_DETAIL }],
        onError(error) {
            dispatch(setGeneratingUserImage(false));
            console.error('Error updating profile:', error);
        },
    });

    const [answersMutation] = useMutation(CREATE_ANSWERS, {
        onCompleted(response) {
            console.log('Quiz answer created successfully', response);
        },
        onError(error) {
            console.log('Error creating quiz answer', error);
        },
    });

    const [updateBase64] = useMutation(UPLOAD_BASE_64);

    const processImageFile = useCallback(async (file: File): Promise<ImageData | null> => {
        try {
            const dataUrl = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => resolve(reader.result as string);
                reader.onerror = (error) => reject(error);
            });

            const arr = dataUrl.split(',');
            if (arr.length < 2) return null;

            const mimeMatch = arr[0].match(/:(.*?);/);
            if (!mimeMatch || !mimeMatch[1]) return null;

            return {
                mimeType: mimeMatch[1],
                data: arr[1],
            };
        } catch (error) {
            console.error('Error processing image file:', error);
            return null;
        }
    }, []);

    const handleUnauthorizedUpload = useCallback(
        async (imageData: ImageData) => {
            console.log('%c Save body full front image for user id', 'color: yellow;font-size:16px', userId);
            try {
                await localforage.setItem(LOCAL_FORAGE_KEY.LOCAL_USER_BODY_FULL_FRONT_IMAGE + userId, imageData);
                const result = await generateModelImageMutation({
                    variables: {
                        input: {
                            modelImageData: imageData,
                        },
                    },
                });
                if (result?.data?.generateModelImage?.modelImageUrl) {
                    setSharedModelImageUrl(result.data.generateModelImage.modelImageUrl);
                }
            } catch (e) {
                console.error('Error saving local user details', e);
                throw e;
            }
        },
        [userId, generateModelImageMutation, setSharedModelImageUrl],
    );

    const handleAuthorizedUpload = useCallback(
        async (imageData: ImageData) => {
            // Upload the photo and get private URL
            const signedUrl = await updateBase64({
                context: {
                    allowAnonymousAuth: true,
                },
                variables: {
                    input: {
                        filename: uuidv4(),
                        mimetype: imageData.mimeType,
                        folder: `private/user-photos/${userId}`,
                        data: imageData.data,
                    },
                },
            });

            const privateUrl = signedUrl?.data?.uploadFileBase64?.url || '';
            if (!privateUrl) {
                console.error('Error uploading user photo');
                throw new Error('Failed to upload image');
            }

            // Update the user quiz answer with the new photo URL
            const quizAnswerVariables = {
                input: {
                    answers: [
                        {
                            contextId: 'my_photo_quiz',
                            contextType: 'quiz',
                            questionId: 'photo_quiz_question_body_full_front',
                            questionSubject: 'TAFFI_MY_PHOTOS',
                            value: privateUrl,
                            valueType: 'UPLOAD_FILE',
                        },
                    ],
                },
            };

            await Promise.all([
                answersMutation({ variables: quizAnswerVariables }),
                updateProfileMutation({
                    variables: {
                        input: {
                            modelImageData: imageData,
                        },
                    },
                }),
            ]);
        },
        [userId, updateBase64, answersMutation, updateProfileMutation],
    );

    const handleUpload = useCallback(
        async (file: File): Promise<string | null> => {
            dispatch(setGeneratingUserImage(true));

            try {
                const imageData = await processImageFile(file);
                if (!imageData) {
                    throw new Error('Failed to process image file');
                }

                if (!authorize.isAuthorize) {
                    await handleUnauthorizedUpload(imageData);
                } else {
                    await handleAuthorizedUpload(imageData);
                }

                // Return data URL for immediate display
                return `data:${imageData.mimeType};base64,${imageData.data}`;
            } catch (error) {
                console.error('Error uploading image:', error);
                dispatch(setGeneratingUserImage(false));
                return null;
            }
        },
        [authorize.isAuthorize, dispatch, processImageFile, handleUnauthorizedUpload, handleAuthorizedUpload],
    );

    return {
        handleUpload,
        isGenerating: generatingModelImage || updateProfileLoading,
    };
};
